import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { View } from "@chef/types/common";
import type { StandaloneBasketVariant } from "@chef/types/basket";

import { PickAndMixQuery } from "../graphql/generated";
import { IDeviationProduct, RootState } from "../";

export interface IRecipeModal {
  open: boolean;
  recipeId: number;
  pickAndMixProduct?: PickAndMixQuery["pickAndMix"][0] | null;
  misc?: {
    portionSize?: string;
    variationId?: string;
  } | null;
  view?: "historic" | "deviation" | "conversion";
}

export interface PauseWeekModalProps {
  open: boolean;
  misc?: {
    deliveryDate?: string;
  } | null;
}
export interface ISubscribedStandaloneModal {
  open: boolean;
  deviationProduct: IDeviationProduct | null;
  productName: string | null;
  maxQtyAllowed: number | null;
}

export interface ICartModal {
  open: boolean;
  week: number | null;
  year: number | null;
}

export interface ISchedulerModal {
  open: boolean;
  week: number;
  year: number;
}

export interface IResumeOngoingDeviationModal {
  open: boolean;
  week: number;
  year: number;
  deviationProducts: IDeviationProduct[];
  calendarBasketProducts: IDeviationProduct[];
  destinationUrl?: string;
}
export interface IPreselectedRecipesModal {
  open: boolean;
  preferenceIds: string[];
}

export interface IPersonalizedRecipesModal {
  open: boolean;
  conceptPreferenceIds: string[];
  tastePreferenceIds: string[];
}

export type StandaloneProductModalProps = {
  id: "STANDALONE_PRODUCT";
  productId: string;
  variationId: string;
  week: number;
  year: number;
  view: View;
  basketType?: StandaloneBasketVariant;
};

export type SimpleModal = {
  open: boolean;
};

type MyMenuSelectWeekModalProps = {
  id: "MY_MENU_SELECT_WEEK";
};

export type RecipeModalProps = {
  id: "RECIPE";
  recipeId: number;
  pickAndMixProduct?: PickAndMixQuery["pickAndMix"][0] | null;
  misc?: {
    portionSize?: string;
    variationId?: string;
  } | null;
  view?: "historic" | "deviation" | "conversion";
};

export type WeekPortionModalProps = {
  id: "WEEK_PORTION";
  week: number;
  year: number;
};

export type ModalData =
  | StandaloneProductModalProps
  | MyMenuSelectWeekModalProps
  | RecipeModalProps
  | WeekPortionModalProps;

export type Modal =
  | {
      open: false;
      data: null;
    }
  | {
      open: true;
      data: ModalData;
    };

const initialState = {
  modal: {
    open: false,
    data: null,
  } as Modal,

  deliveryPause: {
    open: false,
    misc: null,
  } as PauseWeekModalProps,

  subscribedStandalone: {
    open: false,
    deviationProduct: null,
    productName: null,
  } as ISubscribedStandaloneModal,

  cart: {
    open: false,
    week: null,
    year: null,
  } as ICartModal,

  menuDrawer: {
    open: false,
  },

  scheduler: {
    open: false,
    week: 0,
    year: 0,
  } as ISchedulerModal,

  cookies: {
    open: false,
  },

  drawer: {
    open: false,
  } as SimpleModal,

  login: {
    open: false,
  } as SimpleModal,

  resumeOngoingDeviation: {
    open: false,
    week: 0,
    year: 0,
    deviationProducts: [],
    calendarBasketProducts: [],
  } as IResumeOngoingDeviationModal,

  recipeAction: {
    open: false,
  } as SimpleModal,

  preselectedRecipes: {
    open: false,
    preferenceIds: [],
  } as IPreselectedRecipesModal,

  personalizedRecipes: {
    open: false,
    conceptPreferenceIds: [],
    tastePreferenceIds: [],
  } as IPersonalizedRecipesModal,

  myMenuSelectWeek: {
    open: false,
  } as SimpleModal,

  overrideFeatureFlags: {
    open: false,
  } as SimpleModal,
};

export const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<ModalData>) => {
      state.modal = {
        open: true,
        data: action.payload,
      };
    },
    closeModal: (state) => {
      state.modal = {
        open: false,
        data: null,
      };
    },

    setPreselectedRecipesModal: (
      state,
      action: PayloadAction<IPreselectedRecipesModal>,
    ) => {
      state.preselectedRecipes = action.payload;
    },
    resetPreselectedRecipesModal: (state) => ({
      ...state,
      preselectedRecipes: initialState.preselectedRecipes,
    }),

    setPersonalizedRecipesModal: (
      state,
      action: PayloadAction<IPersonalizedRecipesModal>,
    ) => {
      state.personalizedRecipes = action.payload;
    },

    resetPersnalizedRecipesModal: (state) => ({
      ...state,
      personalizedRecipes: initialState.personalizedRecipes,
    }),

    setPauseWeekModal: (state, action: PayloadAction<PauseWeekModalProps>) => {
      state.deliveryPause.open = !action.payload.open;
      state.deliveryPause = action.payload;
    },

    setSubscribedStandaloneModal: (
      state,
      action: PayloadAction<ISubscribedStandaloneModal>,
    ) => {
      state.subscribedStandalone = action.payload;
    },
    resetSubscribedStandaloneModal: (state) => ({
      ...state,
      subscribedStandaloneProduct: initialState.subscribedStandalone,
    }),

    setWeekSchedulerEditorModal: (
      state,
      action: PayloadAction<Partial<ISchedulerModal>>,
    ) => {
      state.scheduler.open = action.payload.open || false;

      if (action.payload.week) {
        state.scheduler.week = action.payload.week;
      }

      if (action.payload.year) {
        state.scheduler.year = action.payload.year;
      }
    },

    toggleCartModal: (
      state,
      action: PayloadAction<Omit<ICartModal, "open">>,
    ) => {
      state.cart.open = !state.cart.open;

      state.cart.week = action.payload.week;
      state.cart.year = action.payload.year;
    },

    setCartModalOpen: (
      state,
      action: PayloadAction<Omit<ICartModal, "open">>,
    ) => {
      state.cart.open = true;

      state.cart.week = action.payload.week;
      state.cart.year = action.payload.year;
    },

    setCartModalClosed: (state) => {
      state.cart.open = false;
    },

    setMenuDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.menuDrawer.open = action.payload;
    },

    toggleMenuDrawer: (state) => {
      state.menuDrawer.open = !state.menuDrawer.open;
    },

    setCookiesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.cookies.open = action.payload;
    },

    toggleCookiesModal: (state) => {
      state.cookies.open = !state.cookies.open;
    },

    toggleDrawerModal: (state) => {
      state.drawer.open = !state.drawer.open;
    },

    showDrawerModal: (state) => {
      state.drawer.open = true;
    },

    hideDrawerModal: (state) => {
      state.drawer.open = false;
    },

    showLoginModal: (state) => {
      state.login.open = true;
    },

    hideLoginModal: (state) => {
      state.login.open = false;
    },

    showRecipeActionModal: (state) => {
      state.recipeAction.open = true;
    },

    hideFavoriteModal: (state) => {
      state.recipeAction.open = false;
    },

    setResumeOngoingDeviationModal: (
      state,
      action: PayloadAction<IResumeOngoingDeviationModal>,
    ) => {
      state.resumeOngoingDeviation = action.payload;
    },

    resetResumeOngoingDeviationModal: (state) => ({
      ...state,
      resumeOngoingDeviation: initialState.resumeOngoingDeviation,
    }),
    showMyMenuSelectWeekModal: (state) => {
      state.myMenuSelectWeek.open = true;
    },

    hideMyMenuSelectWeekModal: (state) => {
      state.myMenuSelectWeek.open = false;
    },

    showOverrideFeatureFlagsModal: (state) => {
      state.overrideFeatureFlags.open = true;
    },

    hideOverrideFeatureFlagsModal: (state) => {
      state.overrideFeatureFlags.open = false;
    },
  },
});

export const {
  openModal,
  closeModal,
  setSubscribedStandaloneModal,
  resetSubscribedStandaloneModal,
  setWeekSchedulerEditorModal,
  toggleCartModal,
  setCartModalClosed,
  setCartModalOpen,
  setMenuDrawerOpen,
  toggleMenuDrawer,
  setCookiesModalOpen,
  toggleCookiesModal,
  setPauseWeekModal,
  toggleDrawerModal,
  showDrawerModal,
  hideDrawerModal,
  showLoginModal,
  hideLoginModal,
  showRecipeActionModal,
  hideFavoriteModal,
  setResumeOngoingDeviationModal,
  resetResumeOngoingDeviationModal,
  setPreselectedRecipesModal,
  resetPreselectedRecipesModal,
  setPersonalizedRecipesModal,
  resetPersnalizedRecipesModal,
  showMyMenuSelectWeekModal,
  hideMyMenuSelectWeekModal,
  showOverrideFeatureFlagsModal,
  hideOverrideFeatureFlagsModal,
} = modalSlice.actions;

export const selectPauseWeekModal = (state: RootState) =>
  state.modal.deliveryPause;

export const selectPauseWeekModalIsOpen = (state: RootState) =>
  state.modal.deliveryPause.open;

export const selectSubscribedStandaloneModal = (state: RootState) =>
  state.modal.subscribedStandalone;

export const selectSchedulerModalIsOpen = (state: RootState) =>
  state.modal.scheduler.open;

export const selectSchedulerModal = (state: RootState) => state.modal.scheduler;

export const selectMenuDrawerIsOpen = (state: RootState) =>
  state.modal.menuDrawer.open;

export const selectCartModalIsOpen = (state: RootState) =>
  state.modal.cart.open;

export const selectCartModal = (state: RootState) => state.modal.cart;

export const selectCookieModalIsOpen = (state: RootState) =>
  state.modal.cookies.open;

export const selectDrawerModalIsOpen = (state: RootState) =>
  state.modal.drawer.open;

export const selectLoginModalIsOpen = (state: RootState) =>
  state.modal.login.open;

export const selectRecipeActionModalIsOpen = (state: RootState) =>
  state.modal.recipeAction.open;

export const selectResumeOngoingDeviationModal = (state: RootState) =>
  state.modal.resumeOngoingDeviation;

export const selectPreselectedRecipesModal = (state: RootState) =>
  state.modal.preselectedRecipes;

export const selectPersonalizedRecipesModal = (state: RootState) =>
  state.modal.personalizedRecipes;

export default modalSlice.reducer;
